import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const DELAYS = [1000, 2000, 4000, 8000];
const GIVE_UP_MS = 60000;
const isUser = (d) => d && typeof d === "object" && typeof d.id === "string" && typeof d.role === "string";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed out (only after a real 401)
  const [boot, setBoot] = useState("checking"); // checking | waking | failed
  const run = useRef(0);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) { setUser(false); return; }
    const id = ++run.current;
    const started = Date.now();
    const wake = setTimeout(() => run.current === id && setBoot("waking"), 2000);
    for (let attempt = 0; run.current === id; attempt++) {
      try {
        const { data } = await api.get("/auth/me", { timeout: 15000 });
        if (!isUser(data)) throw new Error("Unexpected response while starting up");
        if (run.current !== id) return;
        clearTimeout(wake);
        setBoot("checking");
        setUser(data);
        return;
      } catch (e) {
        if (run.current !== id) return;
        if (e?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); clearTimeout(wake); setBoot("checking"); setUser(false); return; }
        const elapsed = Date.now() - started;
        if (elapsed >= GIVE_UP_MS) setBoot("failed");
        await sleep(elapsed >= GIVE_UP_MS ? 15000 : DELAYS[Math.min(attempt, DELAYS.length - 1)]);
      }
    }
  }, []);

  const retry = useCallback(() => { setBoot("waking"); refresh(); }, [refresh]);

  useEffect(() => {
    refresh();
    const onUnauthorized = () => setUser(false);
    window.addEventListener("tl:unauthorized", onUnauthorized);
    const runs = run;
    return () => { runs.current++; window.removeEventListener("tl:unauthorized", onUnauthorized); };
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* token may be expired */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, refresh, retry, boot, waking: boot !== "checking" }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
